// frontend/src/pages/reportpage/reportUtils.js
// Shared helpers used across the Reports page tabs.
// Extracted verbatim from the original ReportsPage.jsx — no logic changes.

export const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

export const monthNames = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

export const paymentDetailsLabel = (method) => {
  const m = String(method || '').toLowerCase();
  if (m.includes('m-pesa') || m.includes('mpesa')) return 'M-Pesa Number / Txn Code *';
  if (m.includes('bank')) return 'Bank Reference / Narration *';
  if (m.includes('cheque') || m.includes('check')) return 'Cheque No / Bank';
  if (m.includes('cash')) return 'Cash Voucher / Note';
  return 'Payment Details';
};

export const needsPaymentDetails = (method) => {
  const m = String(method || '').toLowerCase();
  return m.includes('bank') || m.includes('m-pesa') || m.includes('mpesa');
};

export const formatActor = (actor) => {
  if (!actor) return '-';
  const name = actor.name || '';
  // Show name only, fallback to email if name is not available
  return name || actor.email || '-';
};

export const formatDateTime = (iso) => {
  if (!iso) return '-';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString();
  } catch {
    return iso;
  }
};

// Formats a 'YYYY-MM-DD' string as 'D/M/YYYY' (day first, no leading zeros)
export const formatDayMonthYear = (isoDateStr) => {
  if (!isoDateStr) return '-';
  const parts = String(isoDateStr).split('-');
  if (parts.length !== 3) return isoDateStr;
  const [year, month, day] = parts;
  const d = parseInt(day, 10);
  const m = parseInt(month, 10);
  if (Number.isNaN(d) || Number.isNaN(m) || !year) return isoDateStr;
  return `${d}/${m}/${year}`;
};

export const buildHistoryLines = (h) => {
  if (!h) return [];

  // If backend sends a human summary / changes array, prefer that.
  if (Array.isArray(h.changes) && h.changes.length) {
    const lines = h.changes.map((c) => {
      if (!c) return '';
      const field = c.field || 'Field';
      const from = c.from;
      const to = c.to;
      if (field.toLowerCase().includes('amount')) return `Amount changed from KES ${from} to KES ${to}`;
      return `${field} changed from "${from}" to "${to}"`;
    }).filter(Boolean);
    return lines;
  }

  if (h.summary) {
    return [h.summary];
  }

  // Frontend fallback: compare before/after
  const b = h.before || {};
  const a = h.after || {};
  const keys = [
    ['date', 'Date'],
    ['category', 'Category'],
    ['paidTo', 'Paid To'],
    ['description', 'Description'],
    ['paymentMethod', 'Payment Method'],
    ['paymentDetails', 'Payment Details'],
    ['amount', 'Amount'],
    ['reference', 'Reference'],
    ['notes', 'Notes'],
  ];

  const changes = [];
  for (const [k, label] of keys) {
    const bv = b[k];
    const av = a[k];
    // normalize numbers
    if (k === 'amount') {
      const bn = Number(bv ?? 0);
      const an = Number(av ?? 0);
      if (Number.isFinite(bn) && Number.isFinite(an) && bn !== an) {
        changes.push(`Amount changed from KES ${bn} to KES ${an}`);
      }
    } else {
      const bs = (bv ?? '').toString();
      const as = (av ?? '').toString();
      if (bs !== as) changes.push(`${label} changed from "${bs}" to "${as}"`);
    }
  }

  return changes.length ? changes : [h.action || 'No changes recorded'];
};

export const exportToCSV = (data, filename = 'report.csv') => {
  if (!data || data.length === 0) return;
  const header = Object.keys(data[0]).join(',');
  const csvRows = data.map(row => Object.values(row).map(val => {
    if (val === null || val === undefined) return '';
    return String(val).includes(',') || String(val).includes('"')
      ? `"${String(val).replace(/"/g, '""')}"`
      : String(val);
  }).join(','));
  const csv = [header, ...csvRows].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  window.URL.revokeObjectURL(url);
};
